import React, { useState } from 'react';

const CustomerDetailsModal = ({ customer, services, onClose, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedCustomer, setEditedCustomer] = useState(customer);

  const formatAddress = (address) => {
    if (typeof address === 'object' && address) {
      return `${address.street || ''}, ${address.city || ''}, ${address.state || ''} ${address.zipCode || ''}`.trim();
    }
    return address || 'No address provided';
  };

  const getAddressField = (customer, field) => {
    if (typeof customer.address === 'object' && customer.address) {
      return customer.address[field] || '';
    }
    return customer[field] || '';
  };

  const handleSave = () => {
    onUpdate(editedCustomer);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedCustomer(customer);
    setIsEditing(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-secondary-900">
              {isEditing ? 'Edit Customer' : 'Customer Details'}
            </h2>
            <button
              onClick={onClose}
              className="text-secondary-400 hover:text-secondary-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Customer Information */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">First Name</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedCustomer.firstName || ''}
                        onChange={(e) => setEditedCustomer({...editedCustomer, firstName: e.target.value})}
                        className="input"
                      />
                    ) : (
                      <p className="text-secondary-900">{customer.firstName}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Last Name</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedCustomer.lastName || ''}
                        onChange={(e) => setEditedCustomer({...editedCustomer, lastName: e.target.value})}
                        className="input"
                      />
                    ) : (
                      <p className="text-secondary-900">{customer.lastName}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Email</label>
                  {isEditing ? (
                    <input
                      type="email"
                      value={editedCustomer.email || ''}
                      onChange={(e) => setEditedCustomer({...editedCustomer, email: e.target.value})}
                      className="input"
                    />
                  ) : (
                    <p className="text-secondary-900">{customer.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Phone</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={editedCustomer.phone || ''}
                      onChange={(e) => setEditedCustomer({...editedCustomer, phone: e.target.value})}
                      className="input"
                    />
                  ) : (
                    <p className="text-secondary-900">{customer.phone}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Address</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={formatAddress(editedCustomer.address)}
                      onChange={(e) => setEditedCustomer({...editedCustomer, address: e.target.value})}
                      className="input"
                    />
                  ) : (
                    <p className="text-secondary-900">{formatAddress(customer.address)}</p>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">City</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={getAddressField(editedCustomer, 'city')}
                        onChange={(e) => setEditedCustomer({...editedCustomer, city: e.target.value})}
                        className="input"
                      />
                    ) : (
                      <p className="text-secondary-900">{getAddressField(customer, 'city')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">State</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={getAddressField(editedCustomer, 'state')}
                        onChange={(e) => setEditedCustomer({...editedCustomer, state: e.target.value})}
                        className="input"
                      />
                    ) : (
                      <p className="text-secondary-900">{getAddressField(customer, 'state')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">ZIP</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={getAddressField(editedCustomer, 'zipCode')}
                        onChange={(e) => setEditedCustomer({...editedCustomer, zipCode: e.target.value})}
                        className="input"
                      />
                    ) : (
                      <p className="text-secondary-900">{getAddressField(customer, 'zipCode')}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Service Information */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Service Information</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Status</label>
                    {isEditing ? (
                      <select
                        value={editedCustomer.status || 'active'}
                        onChange={(e) => setEditedCustomer({...editedCustomer, status: e.target.value})}
                        className="input"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="prospect">Prospect</option>
                        <option value="archived">Archived</option>
                      </select>
                    ) : (
                      <span className={`px-2 py-1 rounded text-xs ${customer.status === 'active' ? 'bg-green-100 text-green-800' : customer.status === 'inactive' ? 'bg-gray-100 text-gray-800' : customer.status === 'prospect' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`}>
                        {customer.status || 'active'}
                      </span>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Property Type</label>
                    {isEditing ? (
                      <select
                        value={editedCustomer.propertyType || 'residential'}
                        onChange={(e) => setEditedCustomer({...editedCustomer, propertyType: e.target.value})}
                        className="input"
                      >
                        <option value="residential">Residential</option>
                        <option value="commercial">Commercial</option>
                      </select>
                    ) : (
                      <p className="text-secondary-900">{customer.propertyType || 'residential'}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Property Size</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedCustomer.propertySize || ''}
                      onChange={(e) => setEditedCustomer({...editedCustomer, propertySize: e.target.value})}
                      className="input"
                      placeholder="e.g., 5000 sq ft"
                    />
                  ) : (
                    <p className="text-secondary-900">{customer.propertySize || 'Not specified'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Preferred Contact</label>
                  {isEditing ? (
                    <select
                      value={editedCustomer.preferredContact || 'email'}
                      onChange={(e) => setEditedCustomer({...editedCustomer, preferredContact: e.target.value})}
                      className="input"
                    >
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                    </select>
                  ) : (
                    <p className="text-secondary-900">{customer.preferredContact || 'email'}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Source</label>
                  <p className="text-secondary-900">{customer.source || 'website'}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">Notes</label>
                  {isEditing ? (
                    <textarea
                      value={editedCustomer.notes || ''}
                      onChange={(e) => setEditedCustomer({...editedCustomer, notes: e.target.value})}
                      className="input"
                      rows={3}
                    />
                  ) : (
                    <p className="text-secondary-900">{customer.notes || 'No notes'}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Service Stats */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-4">Service Statistics</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-secondary-50 p-4 rounded-lg">
                <p className="text-sm text-secondary-600">Total Services</p>
                <p className="text-2xl font-bold text-secondary-900">{customer.totalServices || 0}</p>
              </div>
              <div className="bg-secondary-50 p-4 rounded-lg">
                <p className="text-sm text-secondary-600">Total Spent</p>
                <p className="text-2xl font-bold text-secondary-900">${(customer.totalSpent || 0).toLocaleString()}</p>
              </div>
              <div className="bg-secondary-50 p-4 rounded-lg">
                <p className="text-sm text-secondary-600">Last Service</p>
                <p className="text-secondary-900">
                  {customer.lastService ? new Date(customer.lastService.seconds * 1000).toLocaleDateString() : 'Never'}
                </p>
              </div>
              <div className="bg-secondary-50 p-4 rounded-lg">
                <p className="text-sm text-secondary-600">Customer Since</p>
                <p className="text-secondary-900">
                  {customer.createdAt ? new Date(customer.createdAt.seconds * 1000).toLocaleDateString() : 'Unknown'}
                </p>
              </div>
            </div>
          </div>

          {/* Recent Services */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-4">Recent Services</h3>
            {services.length > 0 ? (
              <div className="space-y-3">
                {services.slice(0, 5).map((service) => (
                  <div key={service.id} className="border border-secondary-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-secondary-900">{service.serviceType}</p>
                        <p className="text-sm text-secondary-600">{service.description}</p>
                        <p className="text-xs text-secondary-500">
                          {service.scheduledDate ? new Date(service.scheduledDate).toLocaleDateString() : 'No date'}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 rounded text-xs ${service.status === 'completed' ? 'bg-green-100 text-green-800' : service.status === 'scheduled' ? 'bg-blue-100 text-blue-800' : service.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                          {service.status}
                        </span>
                        {service.cost && (
                          <p className="text-sm font-medium text-secondary-900 mt-1">${service.cost}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-secondary-600">No services recorded yet.</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-secondary-200">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="btn-primary"
                >
                  Save Changes
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn-primary"
                >
                  Edit Customer
                </button>
                <button
                  onClick={onDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Customer
                </button>
                <button
                  onClick={onClose}
                  className="btn-secondary"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetailsModal;